/**
 * Article re-render guard — stops the parent template from re-rendering the
 * product area when an overlay retires its own history entry.
 *
 * The problem in one paragraph:
 *   Snackys' `jtl.article.js` registers `window.addEventListener('popstate', e =>
 *   { if (e.state) that.setArticleContent(e.state.a, …) })` on product-detail
 *   pages (`onLoad`, invoked at window load). `setArticleContent` AJAX-reloads
 *   `#result-wrapper`. Our overlays implement back-to-close by pushing a same-URL
 *   history entry and popping it again, and that pop is indistinguishable, to the
 *   parent, from a user Back-press. So every overlay close re-rendered the product
 *   area — wiping the configuration a customer had just made and leaving
 *   configurable articles impossible to order.
 *
 * Why we guard the METHOD rather than the event:
 *   The listener body is nothing but that one call, so suppressing the call is
 *   exactly equivalent to suppressing the listener — with two advantages. First,
 *   the parent re-runs `onLoad()` after every AJAX content load (`global.js:343`,
 *   `listing.js:89`), each time adding ANOTHER identical listener; there is no
 *   single listener to intercept, but there is a single method. Second, we do not
 *   have to win a listener-ordering race, so the guard does not depend on script
 *   order or on `stopImmediatePropagation`, which would also silence our own
 *   listeners and any plugin's.
 *
 * The alternative we rejected: blanking the state of the history entry beneath
 * ours so the parent's `if (e.state)` guard reads falsy. It works, but it fights
 * the parent over shared mutable state, and its correctness lives in sequencing
 * (deferral class, restore-before-hand-off ordering, a refuse-to-write guard) with
 * a permanent hole when the customer navigates away mid-overlay. See ADR-0007
 * Decision History, 2026-09-24.
 *
 * Bounded by design — this is the codebase's ONLY behavioural wrap of a parent
 * runtime method (ADR-0008's adapter precedent is additive DOM stamping only):
 * one method, one module, installed once, idempotent, and announced in the console
 * when the seam is missing. Do not generalise it into a patching layer without a
 * new ADR.
 *
 * @see src-components/history/overlay-entry-lease.ts (the signal this reads)
 * @see docs/architecture/modals/target-architecture.md §3.2.3
 */

import { hasOverlayEntryPending, isOwnOverlayEntry } from './overlay-entry-lease';

/** What `installArticleRerenderGuard()` did, for the caller to log or assert on. */
export type GuardInstallResult =
  | 'installed'
  | 'already-installed'
  /** No jQuery / `$.evo.article` — not a JTL product page (or a unit test). */
  | 'no-article-api'
  /** The parent object is there but `setArticleContent` is not: the seam moved. */
  | 'seam-missing';

/** The slice of the parent's ArticleClass we touch. */
interface ArticleApi {
  setArticleContent?: unknown;
}
type ArticleAccessor = () => ArticleApi | undefined;

/** Marker on our wrapper so a second install is a no-op rather than a double wrap. */
const GUARD_MARKER = '__datteArticleRerenderGuard';

function getArticleAccessor(): ArticleAccessor | undefined {
  // JTL declares window.$/jQuery loosely; read via a narrow local type.
  const win = window as Window & { jQuery?: { evo?: { article?: ArticleAccessor } }; $?: { jQuery?: never; evo?: { article?: ArticleAccessor } } };
  const jq = win.jQuery ?? win.$;
  return jq?.evo?.article;
}

/**
 * Wrap the parent's `setArticleContent` so it does nothing while an overlay owns a
 * history entry. Safe to call on any page and more than once.
 *
 * Failure is deliberately OPEN: if the seam is gone we log and leave the parent
 * untouched, which restores the old (buggy) behaviour rather than breaking
 * back-to-close or the page. `tests/unit/history/parent-seam.test.ts` fails the
 * build when the parent checkout no longer offers the seam, so a parent upgrade
 * surfaces at the next commit instead of silently in production.
 */
export function installArticleRerenderGuard(): GuardInstallResult {
  const accessor = getArticleAccessor();
  if (typeof accessor !== 'function') return 'no-article-api';

  const article = accessor();
  if (!article) return 'no-article-api';

  // The listener calls `that.setArticleContent` where `that` is the singleton, so
  // patching the singleton is sufficient today. We patch whichever object actually
  // carries the method — the prototype, when the singleton inherits it — so future
  // instances are covered too.
  const target: ArticleApi = Object.prototype.hasOwnProperty.call(article, 'setArticleContent')
    ? article
    : (Object.getPrototypeOf(article) as ArticleApi | null) ?? article;

  const original = target.setArticleContent;
  if (typeof original !== 'function') {
    console.error(
      '[datte] parent seam missing: jtl.article.js setArticleContent not found. ' +
        'Closing an overlay on a product page will re-render the article and discard ' +
        'the customer\'s configuration. See ADR-0007 Decision History 2026-09-24.'
    );
    return 'seam-missing';
  }
  if ((original as unknown as Record<string, unknown>)[GUARD_MARKER]) return 'already-installed';

  const originalFn = original as (...args: unknown[]) => unknown;
  function guarded(this: unknown, ...args: unknown[]): unknown {
    if (hasOverlayEntryPending() || isOwnOverlayEntry(history.state)) {
      // Not a navigation — an overlay of ours retiring its own entry.
      console.debug('[datte] suppressed article re-render during an overlay history pop');
      return undefined;
    }
    return originalFn.apply(this, args);
  }
  (guarded as unknown as Record<string, unknown>)[GUARD_MARKER] = true;
  target.setArticleContent = guarded;
  return 'installed';
}
